import type { LedgerMethod } from '@carsfinders/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

import { t } from '../../../lib/i18n';
import {
  DEPOSITS_QUERY_KEY,
  FINANCE_CLIENTS_QUERY_KEY,
  adminTopUpRequestsQueryKey,
  confirmTopUpRequest,
  financeErrorMessage,
  refusalCode,
  type AdminTopUpRequest,
} from '../api';
import { trapTabKey, useReturnFocusOnUnmount } from '../dialog-focus';
import { SHOW_CARD_METHOD } from '../topup-flags';
import { formatChf } from '../utils';

/** The methods this dialog's select offers — `card` stays out pending (q). */
const METHOD_OPTIONS: LedgerMethod[] = SHOW_CARD_METHOD ? ['bank', 'card', 'cash'] : ['bank', 'cash'];

interface TopUpConfirmDialogProps {
  request: AdminTopUpRequest;
  onClose: () => void;
}

/**
 * «Підтвердити заявку на поповнення» — the manager's record of how the money
 * actually arrived.
 *
 * Amount defaults to the requested one (editable — a bank transfer can land
 * short of a fee). Method defaults to what the client announced but is the
 * MANAGER's field, not an echo: `TopUpConfirmSchema` accepts any
 * `LedgerMethod`, and an operator who received cash for a "bank" request
 * records cash. The receipt field mirrors the server's own rule — required
 * for `cash`, refused empty by the same schema — so the operator sees the
 * problem before the round trip rather than after a 400.
 *
 * A 409 `TOPUP_NOT_OPEN` means someone else (or this operator, twice) already
 * closed it: shown inline, never as a generic failure, with the one useful
 * action — refresh the queue — offered instead of a submit that will only
 * fail again.
 */
export function TopUpConfirmDialog({ request, onClose }: TopUpConfirmDialogProps) {
  const queryClient = useQueryClient();
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);

  const [amountChf, setAmountChf] = useState(String(request.amountChf));
  const [method, setMethod] = useState<LedgerMethod>(
    METHOD_OPTIONS.includes(request.method) ? request.method : 'bank',
  );
  const [receiptRef, setReceiptRef] = useState('');

  useReturnFocusOnUnmount();
  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  const amount = Number.parseInt(amountChf, 10);
  const amountValid = Number.isFinite(amount) && amount > 0;
  // The one client-side rule `TopUpConfirmSchema` also enforces: cash needs a
  // receipt. Every other combination is valid to SUBMIT even if the server
  // still has the last word (amount ceilings, request already closed, …).
  const receiptMissing = method === 'cash' && receiptRef.trim().length === 0;

  const mutation = useMutation({
    mutationFn: () =>
      confirmTopUpRequest(request.id, {
        amountChf: amount,
        method,
        receiptRef: receiptRef.trim() || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminTopUpRequestsQueryKey('open') });
      void queryClient.invalidateQueries({ queryKey: FINANCE_CLIENTS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: DEPOSITS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'finance', 'summary'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'finance', 'journal'] });
      onClose();
    },
    onError: (error) => {
      // 409 TOPUP_NOT_OPEN means someone else already closed it — the panel
      // says «Список оновлено», so it has to actually BE refreshed, not just
      // claim to be. Everything else (validation, a real 500, …) leaves the
      // list alone: retrying it would not help and refetching would be a lie
      // in the other direction.
      if (refusalCode(error) === 'TOPUP_NOT_OPEN') {
        void queryClient.invalidateQueries({ queryKey: adminTopUpRequestsQueryKey('open') });
      }
    },
  });

  const notOpen = refusalCode(mutation.error) === 'TOPUP_NOT_OPEN';
  const errorText = mutation.error
    ? notOpen
      ? t('adminFinance.topUp.confirm.alreadyClosed')
      : (financeErrorMessage(mutation.error) ?? t('adminFinance.topUp.confirm.failed'))
    : null;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      return;
    }
    trapTabKey(dialogRef.current, event);
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-cf-ink/35 p-4 backdrop-blur-[2px]"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={onKeyDown}
        className="flex w-full max-w-[420px] flex-col gap-3.5 rounded-cf-lg border border-cf-line bg-cf-surface p-5 text-left leading-[normal] shadow-[0_24px_60px_rgba(0,0,0,0.16)]"
      >
        <h2 id={titleId} className="text-[18px] font-semibold tracking-[-0.02em]">
          {t('adminFinance.topUp.confirm.title', { reference: request.reference })}
        </h2>
        <p className="text-[13px] leading-[1.5] text-cf-ink-muted">
          {t('adminFinance.topUp.confirm.body', {
            client: request.clientName ?? request.userId,
            amount: formatChf(request.amountChf),
          })}
        </p>

        {notOpen ? (
          <p role="alert" className="rounded-cf-md border border-cf-info bg-cf-info-tint px-3.5 py-2.5 text-[13px] text-cf-info-ink">
            {errorText}
          </p>
        ) : (
          <>
            <label className="flex flex-col gap-1 text-[13px]">
              <span className="text-cf-ink-muted">{t('adminFinance.topUp.confirm.amountLabel')}</span>
              <input
                ref={firstFieldRef}
                value={amountChf}
                inputMode="numeric"
                onChange={(event) => setAmountChf(event.target.value.replace(/\D/g, ''))}
                className="h-10 rounded-cf-md border border-cf-line bg-cf-surface px-3 font-mono text-[14px]"
              />
            </label>

            <label className="flex flex-col gap-1 text-[13px]">
              <span className="text-cf-ink-muted">{t('adminFinance.topUp.confirm.methodLabel')}</span>
              <select
                value={method}
                onChange={(event) => setMethod(event.target.value as LedgerMethod)}
                className="h-10 rounded-cf-md border border-cf-line bg-cf-surface px-3 text-[14px]"
              >
                {METHOD_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {t(`adminFinance.topUp.method.${option}`)}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1 text-[13px]">
              <span className="text-cf-ink-muted">
                {t('adminFinance.topUp.confirm.receiptLabel')}
                {method === 'cash' ? ' *' : ` (${t('adminFinance.topUp.confirm.optional')})`}
              </span>
              <input
                value={receiptRef}
                onChange={(event) => setReceiptRef(event.target.value)}
                aria-invalid={receiptMissing}
                className="h-10 rounded-cf-md border border-cf-line bg-cf-surface px-3 text-[14px]"
              />
              {receiptMissing && (
                <span className="text-[12px] text-cf-danger">
                  {t('adminFinance.topUp.confirm.receiptRequired')}
                </span>
              )}
            </label>

            {errorText && !notOpen && (
              <p role="alert" className="text-[12px] text-cf-danger">
                {errorText}
              </p>
            )}
          </>
        )}

        <div className="mt-1 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-10 rounded-cf-md border border-cf-line bg-cf-surface px-3.5 text-[13px] font-semibold"
          >
            {t('adminFinance.topUp.confirm.cancel')}
          </button>
          {!notOpen && (
            <button
              type="button"
              disabled={!amountValid || receiptMissing || mutation.isPending}
              onClick={() => mutation.mutate()}
              className="h-10 rounded-cf-md bg-cf-brand px-3.5 text-[13px] font-semibold text-white disabled:opacity-40"
            >
              {mutation.isPending
                ? t('adminFinance.topUp.confirm.pending')
                : t('adminFinance.topUp.confirm.submit')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
